import { useCallback, useState } from 'react';
import { useAuth } from '../context/AuthContext';

// Client-side search index for the command palette. Aggregates the same
// list endpoints each page already calls (no dedicated /api/search route)
// and normalizes them into {id, type, title, subtitle, href}. Field access
// falls back defensively since not every entity's exact shape was verified
// end-to-end — a missing field degrades to a blank subtitle, not a crash.
//
// `reachable` keeps a source out of the index for roles whose result would be a
// dead end: a 403 degrades to an empty list already, but a marketer *can* read
// /api/creators while /creators redirects them home, so the hit would bounce.
const SOURCES = [
  {
    type: 'creator',
    reachable: (u) => u?.role !== 'marketer',
    url: '/api/creators',
    listKey: 'creators',
    map: (row) => ({
      title: row.stage_name || row.real_name || 'Untitled creator',
      subtitle: row.real_name && row.real_name !== row.stage_name ? row.real_name : row.email,
      href: `/creators/${row.id}`,
    }),
  },
  {
    type: 'task',
    url: '/api/tasks',
    listKey: 'tasks',
    map: (row) => ({
      title: row.title,
      subtitle: row.status ? row.status.replace('_', ' ') : undefined,
      href: `/tasks/${row.id}`,
    }),
  },
  {
    type: 'team member',
    reachable: (u) => u?.role !== 'marketer',
    url: '/api/team',
    listKey: 'members',
    map: (row) => ({
      title: `${row.first_name || ''} ${row.last_name || ''}`.trim() || row.email,
      subtitle: row.role,
      href: `/team/${row.id}`,
    }),
  },
  {
    type: 'account',
    url: '/api/accounts',
    listKey: 'accounts',
    map: (row) => ({
      title: row.name || row.account_name || row.label || row.username || 'Account',
      subtitle: row.platform || row.category,
      href: '/accounts-library',
    }),
  },
];

export function useSearchIndex() {
  const { token, user } = useAuth();
  const [index, setIndex] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const load = useCallback(async () => {
    if (loaded || loading || !token) return;
    setLoading(true);
    try {
      const results = await Promise.all(
        SOURCES.filter((source) => !source.reachable || source.reachable(user)).map(async (source) => {
          try {
            const res = await fetch(source.url, { headers: { Authorization: `Bearer ${token}` } });
            if (!res.ok) return [];
            const data = await res.json();
            const rows = data[source.listKey] || [];
            return rows.map((row) => ({
              id: `${source.type}-${row.id}`,
              type: source.type,
              ...source.map(row),
            }));
          } catch {
            return [];
          }
        })
      );
      setIndex(results.flat());
      setLoaded(true);
    } finally {
      setLoading(false);
    }
  }, [token, user, loaded, loading]);

  // Mirrors CreatorsPage.jsx's applySearch: lowercase substring match across
  // the visible title/subtitle text.
  const search = useCallback(
    (query) => {
      const q = query.trim().toLowerCase();
      if (!q) return [];
      return index.filter(
        (item) =>
          item.title?.toLowerCase().includes(q) || item.subtitle?.toLowerCase().includes(q)
      );
    },
    [index]
  );

  return { load, search, loading };
}
